import React, { useState, useEffect } from 'react';
import { Button, Card, Badge } from 'react-bootstrap';
import ExerciseLibrary from './components/ExerciseLibrary';
import WorkoutLogger from './components/WorkoutLogger';
import TemplateManager from './components/TemplateManager';
import ProgramManager from './components/ProgramManager';
import ActiveProgramsTable from './components/ActiveProgramsTable';
import CompletedProgramsTable from './components/CompletedProgramsTable';
import WorkoutHistory from './components/WorkoutHistory';
import EditWorkoutModal from './components/EditWorkoutModal';
import Analytics from './components/Analytics';
import Calculators from './components/Calculators';
import SectionHeader from './components/SectionHeader';
import ErrorBoundary from './components/ErrorBoundary';
import { useWorkoutDraft } from './hooks/useWorkoutDraft';
import { workoutAPI } from './services/workoutAPI';
import './styles/WorkoutTracker.scss';

function WorkoutTracker() {
  const [view, setView] = useState('landing');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedProgramWorkout, setSelectedProgramWorkout] = useState(null);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [activePrograms, setActivePrograms] = useState([]);
  const [completedPrograms, setCompletedPrograms] = useState([]);
  const [showCompletedPrograms, setShowCompletedPrograms] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [userId, setUserId] = useState(null);
  const [weeklyMileage, setWeeklyMileage] = useState(null);
  const { hasDraft, draftAge, checkDraft } = useWorkoutDraft(userId);

  // Check if user is logged in and get user ID
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/getUser`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.user) {
          setIsLoggedIn(true);
          setUserId(data.user.id);
        } else {
          throw new Error("User is not logged in");
        }
      })
      .catch((error) => {
        console.error("Error checking if user is logged in:", error);
        setIsLoggedIn(false);
      });
  }, []);

  // Check for draft and fetch active programs when landing page loads
  useEffect(() => {
    if (!isLoggedIn) return;
    checkDraft();
    fetchActivePrograms();
    fetchCompletedPrograms();
    fetchWeeklyMileage();
  }, [checkDraft, isLoggedIn]);

  // Re-check draft status whenever we return to the landing page
  useEffect(() => {
    if (view === 'landing') {
      checkDraft();
    }
  }, [view, checkDraft]);

  console.log('🏠 Landing page - Draft status:', { hasDraft, draftAge });

  const fetchActivePrograms = async () => {
    try {
      const data = await workoutAPI.fetchActivePrograms();
      setActivePrograms(data);
    } catch (err) {
      console.error('Error fetching active programs:', err);
    }
  };

  const fetchCompletedPrograms = async () => {
    try {
      const data = await workoutAPI.fetchCompletedPrograms();
      setCompletedPrograms(data);
    } catch (err) {
      console.error('Error fetching completed programs:', err);
    }
  };

  const fetchWeeklyMileage = async () => {
    try {
      const data = await workoutAPI.fetchWeeklyMileage();
      setWeeklyMileage(data);
    } catch (err) {
      console.error('Error fetching weekly mileage:', err);
    }
  };

  const handleContinueProgram = async (activeProgram) => {
    try {
      const nextWorkout = await workoutAPI.fetchNextWorkout(activeProgram.program_id);
      console.log('📥 Next workout fetched:', nextWorkout);

      if (nextWorkout) {
        // Load template details
        const template = await workoutAPI.fetchTemplateById(nextWorkout.template_id);
        console.log('📋 Template fetched:', template);

        // Parse cardio_overrides if it exists (it's stored as JSON string)
        let cardioOverrides = null;
        if (nextWorkout.cardio_overrides) {
          try {
            cardioOverrides = typeof nextWorkout.cardio_overrides === 'string'
              ? JSON.parse(nextWorkout.cardio_overrides)
              : nextWorkout.cardio_overrides;
            console.log('✅ Parsed cardio_overrides:', cardioOverrides);
          } catch (e) {
            console.error('Error parsing cardio_overrides:', e);
          }
        } else {
          console.log('❌ No cardio_overrides in nextWorkout');
        }

        setSelectedProgramWorkout({
          active_program_id: activeProgram.id,
          program_id: activeProgram.program_id,
          program_name: activeProgram.program_name,
          program_notes: activeProgram.program_notes,
          program_workout_id: nextWorkout.id,
          template_id: nextWorkout.template_id,
          template: template,
          name: nextWorkout.name,
          notes: nextWorkout.notes,
          phase: nextWorkout.phase,
          week: nextWorkout.week,
          day: nextWorkout.day,
          cardio_overrides: cardioOverrides
        });
        setView('logger');
      }
    } catch (err) {
      console.error('Error loading next workout:', err);
    }
  };

  const handleViewProgram = (programId) => {
    setView('programs');
  };

  const handleNavigate = (sectionId) => {
    // Clear any selected items when navigating
    setSelectedTemplate(null);
    setSelectedProgramWorkout(null);
    setView(sectionId);
  };

  const renderLandingPage = () => {
    return (
      <div className="workout-tracker-landing">
        <div className="text-center mb-4">
          <h2 className="mb-2">
            <i className="fas fa-dumbbell me-2"></i>
            Workout Tracker
          </h2>
          <p className="text-muted mb-3" style={{ fontSize: '0.95rem' }}>Track your workouts, build programs, and monitor your progress</p>
        </div>

        {/* Active Programs Section */}
        {activePrograms.length > 0 && (
          <div className="active-programs-section mb-3">
            <h5 className="mb-2">
              <i className="fas fa-fire me-2 text-success"></i>
              Active Programs
            </h5>
            <ActiveProgramsTable
              activePrograms={activePrograms}
              onContinue={handleContinueProgram}
              onRefresh={() => {
                fetchActivePrograms();
                fetchCompletedPrograms();
              }}
            />
          </div>
        )}

        {/* Completed Programs Section */}
        {completedPrograms.length > 0 && (
          <CompletedProgramsTable
            completedPrograms={completedPrograms}
            isExpanded={showCompletedPrograms}
            onToggleExpand={() => setShowCompletedPrograms(!showCompletedPrograms)}
          />
        )}

        <div className="row g-2 g-md-3">
          {/* Start New Workout */}
          <div className="col-6 col-lg-3 col-md-6">
            <Card className="workout-card text-center" onClick={() => setView('logger')} style={{ cursor: 'pointer' }}>
              <Card.Body className="p-3">
                <div className="mb-2 position-relative d-inline-block">
                  <i className="fas fa-play-circle fa-2x text-success"></i>
                  {hasDraft && (
                    <Badge
                      bg="warning"
                      className="workout-draft-indicator"
                      title={`Resume from ${draftAge}`}
                    >
                      <i className="fas fa-save"></i>
                    </Badge>
                  )}
                </div>
                <h6 className="mb-0">Start Workout</h6>
              </Card.Body>
            </Card>
          </div>

          {/* Start from Template */}
          <div className="col-6 col-lg-3 col-md-6">
            <Card className="workout-card text-center" onClick={() => setView('templates')} style={{ cursor: 'pointer' }}>
              <Card.Body className="p-3">
                <div className="mb-2">
                  <i className="fas fa-file-alt fa-2x text-primary"></i>
                </div>
                <h6 className="mb-0">Templates</h6>
              </Card.Body>
            </Card>
          </div>

          {/* Manage Exercises */}
          <div className="col-6 col-lg-3 col-md-6">
            <Card className="workout-card text-center" onClick={() => setView('exercises')} style={{ cursor: 'pointer' }}>
              <Card.Body className="p-3">
                <div className="mb-2">
                  <i className="fas fa-list fa-2x text-info"></i>
                </div>
                <h6 className="mb-0">Exercises</h6>
              </Card.Body>
            </Card>
          </div>

          {/* Programs */}
          <div className="col-6 col-lg-3 col-md-6">
            <Card className="workout-card text-center" onClick={() => setView('programs')} style={{ cursor: 'pointer' }}>
              <Card.Body className="p-3">
                <div className="mb-2">
                  <i className="fas fa-calendar-week fa-2x" style={{ color: '#9b59b6' }}></i>
                </div>
                <h6 className="mb-0">Programs</h6>
              </Card.Body>
            </Card>
          </div>

          {/* Workout History */}
          <div className="col-6 col-lg-3 col-md-6">
            <Card className="workout-card text-center" onClick={() => setView('history')} style={{ cursor: 'pointer' }}>
              <Card.Body className="p-3">
                <div className="mb-2">
                  <i className="fas fa-history fa-2x text-warning"></i>
                </div>
                <h6 className="mb-0">History</h6>
              </Card.Body>
            </Card>
          </div>

          {/* Analytics */}
          <div className="col-6 col-lg-3 col-md-6">
            <Card className="workout-card text-center" onClick={() => setView('analytics')} style={{ cursor: 'pointer' }}>
              <Card.Body className="p-3">
                <div className="mb-2">
                  <i className="fas fa-chart-bar fa-2x" style={{ color: '#17a2b8' }}></i>
                </div>
                <h6 className="mb-0">Analytics</h6>
              </Card.Body>
            </Card>
          </div>

          {/* Calculators */}
          <div className="col-6 col-lg-3 col-md-6">
            <Card className="workout-card text-center" onClick={() => setView('calculators')} style={{ cursor: 'pointer' }}>
              <Card.Body className="p-3">
                <div className="mb-2">
                  <i className="fas fa-calculator fa-2x" style={{ color: '#e67e22' }}></i>
                </div>
                <h6 className="mb-0">Calculators</h6>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Weekly Mileage Stats */}
        <div className="row mt-4">
          <div className="col-12">
            <Card>
              <Card.Body className="p-3">
                <h6 className="mb-3">
                  <i className="fas fa-running me-2"></i>
                  Weekly Mileage
                </h6>
                {weeklyMileage ? (
                  <div className="row">
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <div className="text-muted small mb-1">This Week</div>
                        <div className="h4 mb-0 text-primary">
                          {weeklyMileage.currentWeek.miles.toFixed(1)}
                        </div>
                        <div className="text-muted small">miles</div>
                        {weeklyMileage.currentWeek.workoutCount > 0 && (
                          <div className="text-muted small mt-1">
                            {weeklyMileage.currentWeek.workoutCount} run{weeklyMileage.currentWeek.workoutCount !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-2 bg-light rounded">
                        <div className="text-muted small mb-1">Last Week</div>
                        <div className="h4 mb-0 text-secondary">
                          {weeklyMileage.lastWeek.miles.toFixed(1)}
                        </div>
                        <div className="text-muted small">miles</div>
                        {weeklyMileage.lastWeek.workoutCount > 0 && (
                          <div className="text-muted small mt-1">
                            {weeklyMileage.lastWeek.workoutCount} run{weeklyMileage.lastWeek.workoutCount !== 1 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </div>
                    {weeklyMileage.currentWeek.miles > 0 && weeklyMileage.lastWeek.miles > 0 && (
                      <div className="col-12 mt-2">
                        <div className="text-center">
                          {weeklyMileage.currentWeek.miles > weeklyMileage.lastWeek.miles ? (
                            <span className="text-success small">
                              <i className="fas fa-arrow-up me-1"></i>
                              {((weeklyMileage.currentWeek.miles - weeklyMileage.lastWeek.miles) / weeklyMileage.lastWeek.miles * 100).toFixed(0)}% more than last week
                            </span>
                          ) : weeklyMileage.currentWeek.miles < weeklyMileage.lastWeek.miles ? (
                            <span className="text-muted small">
                              <i className="fas fa-arrow-down me-1"></i>
                              {((weeklyMileage.lastWeek.miles - weeklyMileage.currentWeek.miles) / weeklyMileage.lastWeek.miles * 100).toFixed(0)}% less than last week
                            </span>
                          ) : (
                            <span className="text-muted small">Same as last week</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted text-center py-2 mb-0" style={{ fontSize: '0.9rem' }}>
                    Log cardio workouts to track your weekly mileage
                  </p>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  if (!isLoggedIn) {
    return (
      <h3>
        Please <a className="btn btn-primary" href={`/signin?returnUrl=${encodeURIComponent(window.location.pathname)}`}>
          Sign In
        </a> to view
      </h3>
    );
  }

  return (
    <ErrorBoundary>
      <div className="workout-tracker-container">
        {view === 'landing' && renderLandingPage()}

        {view === 'exercises' && (
          <>
            <SectionHeader
              currentSection="exercises"
              onBack={() => setView('landing')}
              onNavigate={handleNavigate}
            />
            <ExerciseLibrary onBack={() => setView('landing')} />
          </>
        )}

        {view === 'templates' && (
          <>
            <SectionHeader
              currentSection="templates"
              onBack={() => setView('landing')}
              onNavigate={handleNavigate}
            />
            <TemplateManager
              onBack={() => setView('landing')}
              onStartWorkout={(template) => {
                setSelectedTemplate(template);
                setView('logger');
              }}
            />
          </>
        )}

        {view === 'programs' && (
          <>
            <SectionHeader
              currentSection="programs"
              onBack={() => setView('landing')}
              onNavigate={handleNavigate}
            />
            <ProgramManager
              onBack={() => setView('landing')}
            />
          </>
        )}

        {view === 'logger' && (
          <>
            <SectionHeader
              currentSection="logger"
              onBack={() => {
                setSelectedTemplate(null);
                setSelectedProgramWorkout(null);
                setView('landing');
              }}
              onNavigate={handleNavigate}
            />
            <WorkoutLogger
              template={selectedTemplate}
              programWorkout={selectedProgramWorkout}
              userId={userId}
              onFinish={() => {
                setSelectedTemplate(null);
                setSelectedProgramWorkout(null);
                setView('landing');
                fetchActivePrograms(); // Refresh active programs
              }}
              onCancel={() => {
                setSelectedTemplate(null);
                setSelectedProgramWorkout(null);
                setView('landing');
              }}
            />
          </>
        )}

        {view === 'history' && (
          <>
            <SectionHeader
              currentSection="history"
              onBack={() => setView('landing')}
              onNavigate={handleNavigate}
            />
            <WorkoutHistory
              onEditWorkout={(workoutLog) => {
                setEditingWorkout(workoutLog);
                setView('edit-workout');
              }}
            />
          </>
        )}

        {view === 'edit-workout' && editingWorkout && (
          <EditWorkoutModal
            workoutLog={editingWorkout}
            userId={userId}
            onSave={() => {
              setEditingWorkout(null);
              setView('history');
            }}
            onCancel={() => {
              setEditingWorkout(null);
              setView('history');
            }}
          />
        )}

        {view === 'analytics' && (
          <>
            <SectionHeader
              currentSection="analytics"
              onBack={() => setView('landing')}
              onNavigate={handleNavigate}
            />
            <Analytics />
          </>
        )}

        {view === 'calculators' && (
          <>
            <SectionHeader
              currentSection="calculators"
              onBack={() => setView('landing')}
              onNavigate={handleNavigate}
            />
            <Calculators />
          </>
        )}
      </div>
    </ErrorBoundary>
  );
}

export default WorkoutTracker;
